import { DateTime } from 'luxon';
import React from 'react';

import { TransmissionEvent, TransmissionEventChannelProps } from '../types';

import { TransmissionEventRow } from './TransmissionEventRow';

const constructDefaultChannelHeading = (
  channelEvent: TransmissionEvent
): string => {
  return `${channelEvent.toType} to ${channelEvent.to}`;
};

const constructEmailChannelHeading = (
  channelEvent: TransmissionEvent
): string => {
  /**
   * Some email events are recipient-independent and have
   * to="AnyRecipient"|"NoRecipient" or similar (i.e. not an email address).
   * For these, we set the heading to a generic 'Email' instead of the default heading
   */
  return channelEvent.to.includes('@')
    ? constructDefaultChannelHeading(channelEvent)
    : 'Email';
};

const constructChannelHeading = (channelEvent: TransmissionEvent): string => {
  return channelEvent.toType === 'email'
    ? constructEmailChannelHeading(channelEvent)
    : constructDefaultChannelHeading(channelEvent);
};

const sortByTimestampAsc = (
  a: TransmissionEvent,
  b: TransmissionEvent
): number => {
  return (
    DateTime.fromISO(a.timestamp).toMillis() -
    DateTime.fromISO(b.timestamp).toMillis()
  );
};

export const TransmissionEventChannel: React.FC<
  TransmissionEventChannelProps
> = ({ channelEvents, orderCreatedAt }) => {
  const sortedChannelEvents = [...channelEvents].sort(sortByTimestampAsc);

  return (
    <div className="bg-default-100 dark:bg-default-50 p-4 rounded-lg space-y-2">
      <h4 className="font-semibold text-foreground capitalize">
        {constructChannelHeading(sortedChannelEvents[0])}
      </h4>

      <div className="space-y-1">
        {sortedChannelEvents.map((transmissionEvent: TransmissionEvent) => (
          <TransmissionEventRow
            key={transmissionEvent.id}
            transmissionEvent={transmissionEvent}
            orderCreatedAt={orderCreatedAt}
          />
        ))}
      </div>
    </div>
  );
};
